import React, { useEffect, useState } from 'react';
import { Wifi, WifiOff, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';

const OfflineSync = ({ onSyncComplete, isOnline }) => {
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, success, error
  const [pendingOperations, setPendingOperations] = useState([]);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  useEffect(() => {
    loadPendingOperations();
    
    // Auto-sync when coming back online
    if (isOnline && pendingOperations.length > 0) {
      syncPendingOperations();
    }
  }, [isOnline]);

  const loadPendingOperations = () => {
    const pending = JSON.parse(localStorage.getItem('pending_sync_logs') || '[]');
    setPendingOperations(pending);
  };

  const syncPendingOperations = async () => {
    if (!isOnline || pendingOperations.length === 0) return;

    setSyncStatus('syncing');
    
    try {
      const successful = [];
      const failed = [];

      for (const operation of pendingOperations) {
        try {
          await syncOperation(operation);
          successful.push(operation);
        } catch (error) {
          console.error('Sync operation failed:', error);
          failed.push(operation);
        }
      }

      // Update pending operations (keep failed ones)
      localStorage.setItem('pending_sync_logs', JSON.stringify(failed));
      setPendingOperations(failed);

      if (failed.length === 0) {
        setSyncStatus('success');
        setLastSyncTime(new Date());
        
        // Notify parent component
        if (onSyncComplete) {
          onSyncComplete();
        }
      } else {
        setSyncStatus('error');
      }

      // Reset status after a delay
      setTimeout(() => setSyncStatus('idle'), 3000);

    } catch (error) {
      console.error('Sync failed:', error);
      setSyncStatus('error');
      setTimeout(() => setSyncStatus('idle'), 3000);
    }
  };

  const syncOperation = async (operation) => {
    const { data, operation: op } = operation;
    
    // TODO: Replace with actual API calls once endpoint is provided
    // This is where you would implement the actual API sync logic
    
    console.log(`Syncing ${op} operation:`, data);
    
    // Simulate API call
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        // Simulate 90% success rate
        if (Math.random() > 0.1) {
          resolve();
        } else {
          reject(new Error('API sync failed'));
        }
      }, 1000);
    });
  };

  const handleManualSync = () => {
    if (isOnline && pendingOperations.length > 0) {
      syncPendingOperations();
    }
  };

  const clearPendingOperations = () => {
    if (window.confirm('Are you sure you want to clear all pending sync operations? This cannot be undone.')) {
      localStorage.removeItem('pending_sync_logs');
      setPendingOperations([]);
    }
  };

  // Don't render if there are no pending operations and we're online
  if (pendingOperations.length === 0 && isOnline) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-40">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            {isOnline ? (
              <Wifi size={20} className="text-green-600" />
            ) : (
              <WifiOff size={20} className="text-red-600" />
            )}
            <span className="font-medium text-gray-900">
              {isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
          
          {syncStatus === 'syncing' && (
            <RefreshCw size={16} className="text-blue-600 animate-spin" />
          )}
          {syncStatus === 'success' && (
            <CheckCircle size={16} className="text-green-600" />
          )}
          {syncStatus === 'error' && (
            <AlertTriangle size={16} className="text-red-600" />
          )}
        </div>

        {/* Status Message */}
        <div className="text-sm text-gray-600 mb-3">
          {!isOnline && (
            <p>You're working offline. Changes will sync when connection is restored.</p>
          )}
          
          {isOnline && pendingOperations.length > 0 && syncStatus === 'idle' && (
            <p>{pendingOperations.length} operation(s) pending sync.</p>
          )}
          
          {syncStatus === 'syncing' && (
            <p>Syncing {pendingOperations.length} operation(s)...</p>
          )}
          
          {syncStatus === 'success' && (
            <p>All operations synced successfully!</p>
          )}
          
          {syncStatus === 'error' && (
            <p>Some operations failed to sync. Will retry automatically.</p>
          )}
        </div>

        {/* Pending Operations List */}
        {pendingOperations.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-medium text-gray-700 mb-2">Pending Operations:</p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {pendingOperations.slice(0, 5).map((operation, index) => (
                <div key={index} className="text-xs text-gray-600 bg-gray-50 rounded p-2">
                  <span className="font-medium capitalize">{operation.operation}</span>
                  {operation.data.serviceType && (
                    <span className="ml-2">- {operation.data.serviceType}</span>
                  )}
                </div>
              ))}
              {pendingOperations.length > 5 && (
                <div className="text-xs text-gray-500 text-center">
                  +{pendingOperations.length - 5} more...
                </div>
              )}
            </div>
          </div>
        )}

        {/* Last Sync Time */}
        {lastSyncTime && (
          <div className="text-xs text-gray-500 mb-3">
            Last sync: {lastSyncTime.toLocaleTimeString()}
          </div>
        )}

        {/* Actions */}
        <div className="flex space-x-2">
          {isOnline && pendingOperations.length > 0 && syncStatus !== 'syncing' && (
            <button
              onClick={handleManualSync}
              className="flex-1 px-3 py-2 bg-cyan-600 text-white text-sm rounded-lg hover:bg-cyan-700 transition-colors"
            >
              Sync Now
            </button>
          )}
          
          {pendingOperations.length > 0 && (
            <button
              onClick={clearPendingOperations}
              className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OfflineSync;